import React, { useState, useEffect } from 'react';
import { 
  Menu, 
  X, 
  ChevronRight, 
  Users, 
  Landmark, 
  TreePine, 
  ShieldCheck, 
  Calendar, 
  Mail, 
  Facebook, 
  Instagram,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Phone,
  Clock
} from 'lucide-react';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success

  // Mise à jour du titre de page dynamiquement pour le SEO
  useEffect(() => {
    document.title = "Alain Aka 2026 | Candidat Maire Saint-Élix-le-Château - Élections Municipales";
  }, []);

  // Fonction pour le défilement fluide
  const scrollToSection = (id) => {
    setIsMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Gestion du formulaire de contact
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulation d'envoi
    setTimeout(() => {
      setFormStatus('success');
    }, 1500);
  };

  // Données de l'équipe pour meilleure maintenabilité
  const teamMembers = [
    { name: "Alain Aka", photo: "/alain-aka-equipe.jpg", role: "Tête de liste" },
    { name: "Anissa Hamada", photo: "/anissa-hamada.jpg", role: "Colistière" },
    { name: "Audrey Caliz", photo: "/audrey-caliz.jpg", role: "Colistière" },
    { name: "Check Lawal", photo: "/check-lawal.jpg", role: "Colistier" },
    { name: "Christelle Hebert", photo: "/christelle-hebert.jpg", role: "Colistière" },
    { name: "Christelle Martins", photo: "https://placehold.co/600x800/1e40af/ffffff?text=Christelle+Martins", role: "Colistière" },
    { name: "Denis Pol", photo: "/denis-pol.jpg", role: "Colistier" },
    { name: "Fabien Lemenn", photo: "/fabien-lemenn.jpg", role: "Colistier" },
    { name: "Julie Albar", photo: "/julie-albar.jpg", role: "Colistière" },
    { name: "Ludovic Dufraisse", photo: "/ludovic-dufraisse.jpg", role: "Colistier" },
    { name: "Nathalie Marty", photo: "/nathalie-marty.jpeg", role: "Colistière" },
    { name: "Virginia Rousseau", photo: "/virginia-rousseau.jpg", role: "Colistière" },
    { name: "Xavier Diaz", photo: "/xavier-diaz.jpg", role: "Colistier" },
    { name: "Yannick Jaccard", photo: "/yannick-jaccard.jpg", role: "Colistier" },
    { name: "Yannick Lombard", photo: "/yannick-lombard.jpg", role: "Colistier" },
    { name: "Brigitte Bazin", photo: "https://placehold.co/600x800/1e40af/ffffff?text=Brigitte+Bazin", role: "Colistière" },
    { name: "Alex Papa", photo: "/alex-papa.jpeg", role: "Colistier" }
  ];

  // Données des événements
  const events = [
    { date: "15 Fév", title: "Réunion Publique - Lancement de campagne", loc: "Salle des Fêtes", time: "19h30" },
    { date: "22 Fév", title: "Café-Débat : La Sécurité à Saint-Élix", loc: "Place du Château", time: "10h00" },
    { date: "01 Mar", title: "Rencontre Quartier Nord", loc: "École élémentaire", time: "18h00" },
  ];

  // Piliers du programme
  const programPillars = [
    {
      icon: ShieldCheck,
      title: "Sécurité & Voirie",
      description: "Réfection des trottoirs, éclairage intelligent et sécurisation des abords de l'école.",
      items: ["Plan trottoirs 2026-2028", "Voisins vigilants", "Éclairage LED écologique"],
      color: "blue"
    },
    {
      icon: Landmark,
      title: "Patrimoine",
      description: "Mise en valeur du Château et de l'église pour développer un tourisme raisonné.",
      items: ["Parcours historique", "Soutien rénovation façades", "Visites guidées"],
      color: "amber"
    },
    {
      icon: TreePine,
      title: "Cadre de Vie",
      description: "Un village fleuri, propre et respectueux de l'environnement.",
      items: ["Zéro pesticide", "Nouveaux jardins partagés", "Compostage collectif"],
      color: "green"
    },
    {
      icon: Users,
      title: "Lien Social",
      description: "Soutien aux associations, aux aînés et activités pour la jeunesse.",
      items: ["Navette séniors", "Espace Jeunes", "Fête des voisins annuelle"],
      color: "rose"
    },
    {
      icon: Landmark,
      title: "Commerces",
      description: "Dynamiser le commerce local et favoriser l'installation de nouvelles activités.",
      items: ["Soutien aux commerçants", "Marché hebdomadaire", "Circuits courts locaux"],
      color: "purple"
    },
    {
      icon: Users,
      title: "Jeunesse & Sport",
      description: "Offrir aux jeunes des infrastructures sportives et des activités adaptées.",
      items: ["Terrain multisports", "Club des jeunes", "Cours d'activités gratuites"],
      color: "indigo"
    }
  ];

  const colorClasses = {
    blue: { bg: "bg-blue-100", text: "text-blue-800" },
    amber: { bg: "bg-amber-100", text: "text-amber-800" },
    green: { bg: "bg-green-100", text: "text-green-800" },
    rose: { bg: "bg-rose-100", text: "text-rose-800" },
    purple: { bg: "bg-purple-100", text: "text-purple-800" },
    indigo: { bg: "bg-indigo-100", text: "text-indigo-800" }
  };

  return (
    <div className="font-sans text-slate-800 bg-slate-50">
      
      {/* Skip to main content - Accessibilité */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-blue-800 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        Aller au contenu principal
      </a>

      {/* --- NAVIGATION --- */}
      <nav 
        className="fixed w-full z-50 bg-white/95 backdrop-blur-sm shadow-sm border-b border-slate-100"
        role="navigation"
        aria-label="Navigation principale"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo / Nom */}
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => scrollToSection('accueil')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && scrollToSection('accueil')}
              aria-label="Retour à l'accueil - Alain Aka 2026"
            >
              <div className="w-10 h-10 bg-blue-800 rounded-full flex items-center justify-center text-white font-bold text-xl mr-3" aria-hidden="true">
                A
              </div>
              <div>
                <h1 className="text-xl font-bold text-blue-900 leading-none">ALAIN AKA</h1>
                <p className="text-xs text-slate-500 font-medium tracking-wide">SAINT-ÉLIX-LE-CHÂTEAU 2026</p>
              </div>
            </div>

            {/* Menu Desktop */}
            <div className="hidden md:flex space-x-8" role="menubar">
              {['Le Candidat', 'Notre Projet', 'L\'Équipe', 'Agenda'].map((item) => (
                <button 
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-').replace("'", '-'))}
                  className="text-slate-600 hover:text-blue-800 font-medium transition-colors"
                  role="menuitem"
                  aria-label={`Aller à la section ${item}`}
                >
                  {item}
                </button>
              ))}
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-blue-800 hover:bg-blue-700 text-white px-5 py-2 rounded-full font-medium transition-colors shadow-md hover:shadow-lg"
                role="menuitem"
                aria-label="Aller au formulaire de contact pour nous rejoindre"
              >
                Nous rejoindre
              </button>
            </div>

            {/* Menu Mobile Button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="text-slate-700 hover:text-blue-800 p-2"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              >
                {isMenuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {isMenuOpen && (
          <div 
            id="mobile-menu"
            className="md:hidden bg-white border-t border-slate-100 absolute w-full"
            role="menu"
            aria-label="Menu de navigation mobile"
          >
            <div className="px-4 pt-2 pb-6 space-y-2 shadow-xl">
              {['Le Candidat', 'Notre Projet', 'L\'Équipe', 'Agenda', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-').replace("'", '-'))}
                  className="block w-full text-left px-3 py-4 text-base font-medium text-slate-700 hover:bg-slate-50 hover:text-blue-800 rounded-lg border-b border-slate-50 last:border-0"
                  role="menuitem"
                  aria-label={`Aller à la section ${item}`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* --- MAIN CONTENT --- */}
      <main id="main-content">

        {/* --- HERO HEADER --- */}
        <header 
          id="accueil" 
          className="relative pt-20 flex items-center justify-center min-h-[85vh] overflow-hidden"
          aria-label="Section d'accueil"
        >
          {/* Background Image */}
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <img 
              src="https://images.unsplash.com/photo-1518316583997-0c43db891961?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt=""
              role="presentation"
              className="w-full h-full object-cover opacity-20"
              loading="eager"
              width="2070"
              height="1380"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/0 via-white/20 to-slate-50"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center md:text-left flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 space-y-6">
              <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold mb-2">
                <time dateTime="2026">Élections Municipales 2026</time>
              </div>
              <h2 className="text-5xl md:text-6xl font-extrabold text-slate-900 leading-tight">
                Un nouvel élan pour <span className="text-blue-800">Saint-Élix</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-lg mx-auto md:mx-0">
                Construisons ensemble une commune dynamique, solidaire et fière de son patrimoine.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start pt-4">
                <button 
                  onClick={() => scrollToSection('notre-projet')} 
                  className="px-8 py-4 bg-blue-800 text-white rounded-lg font-bold shadow-lg hover:bg-blue-700 transition-all flex items-center justify-center"
                  aria-label="Découvrir notre programme électoral"
                >
                  Découvrir le projet <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </button>
                <button 
                  onClick={() => scrollToSection('le-candidat')} 
                  className="px-8 py-4 bg-white text-slate-700 border-2 border-slate-200 rounded-lg font-bold hover:border-blue-800 hover:text-blue-800 transition-all"
                  aria-label="En savoir plus sur Alain Aka"
                >
                  Qui suis-je ?
                </button>
              </div>
            </div>
            
            {/* Image Candidat - Alain Aka */}
            <div className="md:w-1/2 mt-12 md:mt-0 relative flex justify-center">
              <div className="relative w-72 h-72 md:w-96 md:h-96">
                <div className="absolute inset-0 bg-blue-800 rounded-full opacity-10 transform translate-x-4 translate-y-4" aria-hidden="true"></div>
                <img 
                  src="/alain-aka.webp"
                  alt="Portrait d'Alain Aka, candidat aux élections municipales 2026 à Saint-Élix-le-Château" 
                  className="w-full h-full object-cover rounded-full border-8 border-white shadow-2xl relative z-10"
                  loading="eager"
                  width="384"
                  height="384"
                />
                <div className="absolute bottom-0 right-0 md:right-8 z-20 bg-white p-4 rounded-xl shadow-lg border border-slate-100 max-w-xs text-left">
                  <p className="font-bold text-blue-900">{'"À votre écoute."'}</p>
                  <p className="text-xs text-slate-500">Alain Aka</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* --- LE CANDIDAT --- */}
        <section 
          id="le-candidat" 
          className="py-20 bg-white"
          aria-labelledby="candidat-title"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <article className="flex flex-col md:flex-row items-center gap-12">
              <figure className="md:w-1/2 relative">
                <img 
                  src="/alain-aka.webp"
                  alt="Alain Aka dans son environnement de travail à Saint-Élix-le-Château" 
                  className="rounded-2xl shadow-xl w-full h-80 object-cover"
                  loading="lazy"
                  width="600"
                  height="320"
                />
                <div className="absolute -bottom-6 -right-6 bg-amber-500 text-white p-6 rounded-xl hidden md:block shadow-lg" aria-label="20 ans d'engagement local">
                  <p className="font-bold text-2xl">20 ans</p>
                  <p className="text-sm opacity-90">{"d'engagement local"}</p>
                </div>
              </figure>
              <div className="md:w-1/2 space-y-6">
                <span className="text-sm font-bold text-amber-600 uppercase tracking-wider">Biographie</span>
                <h2 id="candidat-title" className="text-3xl font-bold text-slate-900">Passionné par notre village</h2>
                <p className="text-slate-600 leading-relaxed">
                  Chères Saint-Élixoises, chers Saint-Élixois,
                </p>
                <p className="text-slate-600 leading-relaxed">
                  {"Habitant de Saint-Élix-le-Château depuis plus de deux décennies, j'ai vu notre commune évoluer. Père de famille et engagé dans le tissu associatif local, je connais la valeur de l'entraide qui caractérise notre village."}
                </p>
                <p className="text-slate-600 leading-relaxed">
                  {"Mon expérience professionnelle dans la gestion de projets m'a appris la rigueur nécessaire à la gestion d'un budget communal. Aujourd'hui, je souhaite mettre cette énergie au service de tous, sans étiquette politique, avec pour seule boussole l'intérêt général."}
                </p>
                
                <ul className="grid grid-cols-2 gap-4 pt-4" aria-label={"Valeurs d'Alain Aka"}>
                  {['Proximité', 'Transparence', 'Expérience', 'Dynamisme'].map((value) => (
                    <li key={value} className="flex items-center space-x-3">
                      <CheckCircle2 className="text-green-600 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                      <span className="font-medium text-slate-700">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </section>

        {/* --- PROGRAMME --- */}
        <section 
          id="notre-projet" 
          className="py-20 bg-slate-50"
          aria-labelledby="programme-title"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="programme-title" className="text-3xl font-bold text-slate-900 mb-4">Nos 6 Piliers pour Saint-Élix</h2>
              <p className="text-slate-600">
                Un projet réaliste et ambitieux, construit à partir de vos attentes lors de nos réunions de consultation.
              </p>
            </header>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list" aria-label="Les 6 piliers de notre programme">
              {programPillars.map((pillar, index) => {
                const IconComponent = pillar.icon;
                const colors = colorClasses[pillar.color];
                return (
                  <article 
                    key={index} 
                    className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                    role="listitem"
                  >
                    <div className={`w-12 h-12 ${colors.bg} ${colors.text} rounded-xl flex items-center justify-center mb-6`} aria-hidden="true">
                      <IconComponent size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{pillar.title}</h3>
                    <p className="text-slate-600 text-sm mb-4">{pillar.description}</p>
                    <ul className="space-y-2 text-sm text-slate-500" aria-label={`Actions pour ${pillar.title}`}>
                      {pillar.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* --- EQUIPE --- */}
        <section 
          id="l-equipe" 
          className="py-20 bg-white border-t border-slate-100"
          aria-labelledby="equipe-title"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="text-center mb-16">
              <h2 id="equipe-title" className="text-3xl font-bold text-slate-900">Une équipe à votre image</h2>
              <p className="text-slate-600 mt-4">
                Des femmes et des hommes de tous les quartiers, unis par une même vision pour Saint-Élix.
              </p>
            </header>

            {/* Photo d'équipe de groupe */}
            <figure className="mb-16">
              <img 
                src="/equipe-groupe.png"
                alt="Photo de groupe de l'équipe de campagne d'Alain Aka pour les élections municipales 2026 à Saint-Élix-le-Château - 17 colistiers réunis" 
                className="w-full rounded-2xl shadow-2xl"
                loading="lazy"
                width="1200"
                height="600"
              />
              <figcaption className="sr-only">{"L'équipe de campagne au complet pour les élections municipales 2026"}</figcaption>
            </figure>

            {/* Grille des membres individuels */}
            <div 
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
              role="list"
              aria-label="Membres de l'équipe de campagne"
            >
              {teamMembers.map((member, i) => (
                <article 
                  key={i} 
                  className="group relative overflow-hidden rounded-lg aspect-[3/4] shadow-md hover:shadow-xl transition-shadow"
                  role="listitem"
                >
                  <img 
                    src={member.photo}
                    alt={`Portrait de ${member.name}, ${member.role} de la liste Alain Aka 2026`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    width="300"
                    height="400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent flex flex-col justify-end p-4">
                    <p className="text-white font-bold text-base">{member.name}</p>
                  </div>
                </article>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-6">
                {"Une équipe complète et engagée pour l'avenir de Saint-Élix-le-Château."}
              </p>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="inline-flex items-center px-6 py-3 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
                aria-label="Rejoindre l'équipe de campagne"
              >
                Rejoindre {"l'équipe"} <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        {/* --- AGENDA --- */}
        <section 
          id="agenda" 
          className="py-20 bg-blue-900 text-white"
          aria-labelledby="agenda-title"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="flex flex-col md:flex-row justify-between items-end mb-10 border-b border-blue-800 pb-6">
              <div>
                <h2 id="agenda-title" className="text-3xl font-bold">{"L'Agenda de campagne"}</h2>
                <p className="text-blue-200 mt-2">Venez échanger avec nous.</p>
              </div>
              <button 
                className="mt-4 md:mt-0 flex items-center text-blue-200 hover:text-white transition-colors"
                aria-label="Voir tout le calendrier des événements de campagne"
              >
                Voir tout le calendrier <ChevronRight size={20} aria-hidden="true" />
              </button>
            </header>

            <div className="space-y-4" role="list" aria-label="Prochains événements de campagne">
              {events.map((event, idx) => (
                <article 
                  key={idx} 
                  className="flex flex-col md:flex-row items-center bg-blue-800/50 p-6 rounded-xl hover:bg-blue-800 transition-colors cursor-pointer border border-blue-700"
                  role="listitem"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-white text-blue-900 rounded-lg flex flex-col items-center justify-center font-bold mb-4 md:mb-0 md:mr-6" aria-hidden="true">
                    <span className="text-xs uppercase tracking-wide">{event.date.split(' ')[1]}</span>
                    <span className="text-2xl leading-none">{event.date.split(' ')[0]}</span>
                  </div>
                  <div className="flex-grow text-center md:text-left">
                    <h3 className="text-xl font-bold">{event.title}</h3>
                    <div className="flex items-center justify-center md:justify-start mt-1 text-blue-200 space-x-4">
                      <span className="flex items-center text-sm">
                        <MapPin size={14} className="mr-1" aria-hidden="true" /> 
                        <span aria-label={`Lieu: ${event.loc}`}>{event.loc}</span>
                      </span>
                      <span className="flex items-center text-sm">
                        <Calendar size={14} className="mr-1" aria-hidden="true" /> 
                        <time aria-label={`Horaire: ${event.time}`}>{event.time}</time>
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <button 
                      className="px-4 py-2 border border-blue-400 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                      aria-label={`S'inscrire à l'événement ${event.title}`}
                    >
                      {"S'inscrire"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* --- CONTACT --- */}
        <section 
          id="contact" 
          className="py-20 bg-white"
          aria-labelledby="contact-title"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <header className="text-center mb-12">
              <h2 id="contact-title" className="text-3xl font-bold text-slate-900">Rejoignez le mouvement</h2>
              <p className="text-slate-600 mt-2">
                {"Une question ? Envie d'aider pour la distribution de tracts ? Écrivez-nous."}
              </p>
            </header>

            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
              {formStatus === 'success' ? (
                <div className="p-12 text-center" role="status" aria-live="polite">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                    <CheckCircle2 size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Message envoyé !</h3>
                  <p className="text-slate-600">Merci de votre soutien. Nous vous répondrons très vite.</p>
                  <button 
                    onClick={() => setFormStatus('idle')} 
                    className="mt-6 text-blue-800 font-medium hover:underline"
                    aria-label="Envoyer un autre message de contact"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form 
                  onSubmit={handleFormSubmit} 
                  className="p-8 md:p-12 space-y-6"
                  aria-label="Formulaire de contact"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                        Nom complet <span className="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name"
                        required 
                        autoComplete="name"
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                        placeholder="Jean Dupont" 
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                        Email <span className="text-red-500" aria-hidden="true">*</span>
                      </label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email"
                        required 
                        autoComplete="email"
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                        placeholder="jean@exemple.com" 
                        aria-required="true"
                      />
                    </div>
                  </div>
                  
                  <fieldset>
                    <legend className="block text-sm font-medium text-slate-700 mb-3">Je souhaite :</legend>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          name="newsletter"
                          className="rounded text-blue-800 focus:ring-blue-500 h-5 w-5 border-slate-300" 
                        />
                        <span className="text-slate-600">Recevoir la newsletter</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          name="benevole"
                          className="rounded text-blue-800 focus:ring-blue-500 h-5 w-5 border-slate-300" 
                        />
                        <span className="text-slate-600">Devenir bénévole</span>
                      </label>
                    </div>
                  </fieldset>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                    <textarea 
                      id="message" 
                      name="message"
                      rows="4" 
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                      placeholder="Votre question ou suggestion pour Alain..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    disabled={formStatus === 'submitting'}
                    className="w-full bg-blue-800 hover:bg-blue-700 text-white font-bold py-4 rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                    aria-busy={formStatus === 'submitting'}
                  >
                    {formStatus === 'submitting' ? 'Envoi en cours...' : 'Envoyer mon message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

      </main>

      {/* --- FOOTER --- */}
      <footer 
        className="bg-slate-900 text-slate-400 py-12"
        role="contentinfo"
        aria-label="Pied de page"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-white text-lg font-bold mb-4">Alain Aka 2026</h3>
              <p className="text-sm mb-4">
                Site officiel de campagne pour les élections municipales de Saint-Élix-le-Château.
              </p>
              <address className="not-italic">
                <div className="flex space-x-4">
                  <a 
                    href="https://facebook.com/alainaka2026" 
                    className="text-slate-400 hover:text-white transition-colors"
                    aria-label="Suivez Alain Aka sur Facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Facebook size={20} aria-hidden="true" />
                  </a>
                  <a 
                    href="https://instagram.com/alainaka2026" 
                    className="text-slate-400 hover:text-white transition-colors"
                    aria-label="Suivez Alain Aka sur Instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram size={20} aria-hidden="true" />
                  </a>
                  <a 
                    href="mailto:contact@alain-aka-2026.fr" 
                    className="text-slate-400 hover:text-white transition-colors"
                    aria-label="Envoyer un email à l'équipe de campagne"
                  >
                    <Mail size={20} aria-hidden="true" />
                  </a>
                </div>
              </address>
            </div>
            <nav aria-label="Liens rapides">
              <h3 className="text-white text-lg font-bold mb-4">Liens rapides</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button 
                    onClick={() => scrollToSection('le-candidat')} 
                    className="hover:text-white transition-colors"
                  >
                    Le Candidat
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('notre-projet')} 
                    className="hover:text-white transition-colors"
                  >
                    Le Programme
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('agenda')} 
                    className="hover:text-white transition-colors"
                  >
                    Réunions publiques
                  </button>
                </li>
              </ul>
            </nav>
            <div>
              <h3 className="text-white text-lg font-bold mb-4">Permanence</h3>
              <address className="not-italic text-sm">
                <p className="flex items-start mb-2">
                  <MapPin size={16} className="mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>
                    Local de campagne<br/>
                    2 rue de la Mairie<br/>
                    31430 Saint-Élix-le-Château
                  </span>
                </p>
                <p className="flex items-center text-amber-500 mt-4">
                  <Clock size={16} className="mr-2 flex-shrink-0" aria-hidden="true" />
                  <span>Ouvert le samedi matin de 10h à 12h</span>
                </p>
              </address>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs">
            <p>
              <span aria-label="Copyright">©</span> <time dateTime="2025">2025</time> Alain Aka - Tous droits réservés.
            </p>
            <nav className="flex space-x-6 mt-4 md:mt-0" aria-label="Liens légaux">
              <a href="/mentions-legales" className="hover:text-white transition-colors">Mentions légales</a>
              <a href="/politique-confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</a>
              <a href="/comptes-campagne" className="hover:text-white transition-colors">Comptes de campagne</a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
